import React, { useMemo, useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { SegmentQueue as SegmentQueueComponent } from 'Components/admin/segmentqueue'
import {
  Body,
  BodyTitle,
  BodyActions,
  BodyHeader,
  BodyContent,
} from 'Components/shared/body'
import { TableFooter } from 'Components/shared/table'
import { TablePagination } from 'Containers/shared/tablePagination'
import { SegmentsDetails } from 'Containers/shared/contracts/segments'
import { DefaultButton } from 'Components/shared/buttons'

import { Loading } from 'Utils/loading'
import {
  setSelectAll,
  setSelectedSegments,
  selectSegmentQueue,
  clearSelected,
} from './SegmentQueueSlice'

import { usePagination } from 'Hooks/usePagination'
import { useToggle } from 'Hooks/useToggle'
import { Permissions } from 'Configs/userPermissions'
import { useHasPermission } from 'Hooks/useHasPermission'

import {
  useGetSegmentsQueueQuery,
  useDeleteSegmentMutation,
  useProcessSegmentOrderMutation,
} from 'Services/segments/segments'
import { ContractSource, SegmentAction } from 'Types/index'
import { SearchComponent } from 'Components/connectedComponents/ui'

const sortColumnData = (sortOrder) => [
  { columnName: 'College ID', columnWidth: 80 },
  { columnName: 'Segment Name', columnWidth: 180 },
  { columnName: 'College Name', columnWidth: 180, sortOrder: sortOrder, sortBy: 'college_name' },
  { columnName: 'Contract Related', columnWidth: 180 },
  { columnName: 'Leads Request', columnWidth: 100 },
  { columnName: 'Dedup Records', columnWidth: 100 },
]

export const SegmentQueue = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const PAGE_SIZE = 20

  const { currentPage, handlePageChange, setCurrentPage } = usePagination()
  const [segmentDetails, setSegmentDetails] = useToggle(false)
  const [segmentRowIdx, setSegmentRowIdx] = useState(-1)
  const [sortOrder, setSortOrder] = useState('asc')

  const { selectAll, selectedSegments } =
    useSelector(selectSegmentQueue)

    const {search: {value: searchString}} =
    useSelector(state => state)

  const { hasPermission: candProcessSegments, isFetching } = useHasPermission(
    Permissions.ProcessOnDemandPendingSegments
  )

  const { data: segmentData, isLoading, isFetching: isFetchingQueue, refetch } = useGetSegmentsQueueQuery({
    page: currentPage,
    order: sortOrder,
    searchString 
  })
  const [deleteSegment, { isLoading: isDeleting }] = useDeleteSegmentMutation()
  const [processOrder, { isLoading: isProcessing }] =
    useProcessSegmentOrderMutation()

  const initialRowIdxs = new Array(PAGE_SIZE).fill(false)

  // sets select all checkbox on page change
  useEffect(() => {
      if(!isLoading && (selectedSegments.length >= segmentData?.records.length || selectAll)) {
        dispatch(setSelectAll(segmentData?.records.every(segment => selectedSegments.includes(segment.id))))
      }
  }, [segmentData])

  useEffect(() => {
    if(searchString && searchString.length > 2) {
      currentPage > 0 && setCurrentPage(0)
      refetch()
    }
  }, [searchString])



  const flattenSegments = useMemo(() => {
    let rows = []
    if (segmentData) {
      segmentData?.records?.forEach((segment: any) => {
        rows.push([
          segment.id,
          segment.institution_id,
          segment.name,
          segment.institution_name,
          segment.contract_label,
          segment.lead_total,
          segment.dedupe_count ?? 0,
        ])
      })
    }
    return rows
  }, [segmentData])

  const handleAllClick = (flag: boolean) => {
    dispatch(setSelectAll(flag))
    if (flag) {
      dispatch(setSelectedSegments({
        segments: [...new Set([...selectedSegments ,...segmentData?.records?.map(segment => segment.id)])],
      }))
    } else {
      const idsToRemove = segmentData?.records?.map(record => record.id)
      dispatch(setSelectedSegments({ segments: selectedSegments.filter(segment => !idsToRemove.includes(segment)) }))
    }
  }

  const handleRowClick = (rowIdx: number) => {
    setSegmentDetails()
    setSegmentRowIdx(rowIdx)
  }

  const handleCheckboxClick = (event, rowIdx: number) => {
    event.stopPropagation()
    let segments = [rowIdx]
    if(selectedSegments){
      if(selectedSegments.includes(rowIdx)){
        const indexToRemove = selectedSegments.indexOf(rowIdx)
        segments = [...selectedSegments.slice(0, indexToRemove), ...selectedSegments.slice(indexToRemove + 1)]
      } else {
        segments = [...selectedSegments, rowIdx]
        dispatch(setSelectAll(segmentData?.records.every(segment => segments.includes(segment.id))))
      }
    }
    dispatch(setSelectedSegments({ segments }))
  }

  const handleDeleteSegment = () => {
    deleteSegment({
      ids: segmentData?.records[segmentRowIdx].id,
    })
    closeSegmentDetailsDialog()
  }

  const handleEditSegment = (institutionId: string) => {
    closeSegmentDetailsDialog()
    navigate(
      `/institution/${institutionId}/prospects/contracts/segment/${segmentData?.records[segmentRowIdx].id}` +
        `?action=${SegmentAction.edit}`
    )
  }

  const handleDuplicateSegment = (institutionId: string) => {
    closeSegmentDetailsDialog()
    navigate(
      `/institution/${institutionId}/prospects/contracts/segment/${segmentData?.records[segmentRowIdx].id}` +
        `?action=${SegmentAction.duplicate}`
    )
  }

  const handleProcessOrders = () => {
    processOrder({ segment_ids: selectedSegments })
    dispatch(clearSelected())
  }

  const closeSegmentDetailsDialog = () => {
    setSegmentDetails()
  }

  const isNothingSelected = !Object.keys(selectedSegments).length || Object.keys(selectedSegments).reduce((acc, key) => acc + selectedSegments[key].length, 0) === 0

  return (
    <Body>
      {isLoading || isDeleting || isProcessing || isFetching || isFetchingQueue ? (
        <Loading />
      ) : null}
      <BodyHeader>
        <BodyTitle>Prospect Segments in Queue</BodyTitle>
        <SearchComponent handlePageChange={handlePageChange} />
        {candProcessSegments && (
          <BodyActions>
            <DefaultButton onClick={handleProcessOrders} disabled={isNothingSelected}>
              Process Orders
            </DefaultButton>
          </BodyActions>
        )}
      </BodyHeader>
      <BodyContent>
        {segmentData?.total_records > 0 ? (
          <>
            <SegmentQueueComponent
              columnData={sortColumnData(sortOrder)}
              setSortOrder={setSortOrder}
              rows={flattenSegments}
              rowIdxs={selectedSegments}
              selectAll={selectAll}
              handleAllClick={handleAllClick}
              handleRowClick={handleRowClick}
              handleCheckboxClick={handleCheckboxClick}
            />
            {segmentDetails && (
              <SegmentsDetails
                disabledContract={!candProcessSegments}
                segmentDetailsDialog={segmentDetails}
                closeSegmentDetailsDialog={closeSegmentDetailsDialog}
                chosenSegment={segmentData?.records[segmentRowIdx]}
                contractSource={ContractSource.candidates}
                segmentLeadCapTotal={
                  segmentData?.records[segmentRowIdx]?.lead_total
                }
                handleDeleteSegment={handleDeleteSegment}
                handleEditSegment={handleEditSegment}
                handleDuplicateSegment={handleDuplicateSegment}
                contract={{
                  audience_type:
                    segmentData?.records[segmentRowIdx]?.contract_audience_type,
                  label:
                    segmentData?.records[segmentRowIdx]?.contract_label,
                }}
                isSegmentQueue={true}
              />
            )}
            <TableFooter>
              {segmentData?.total_pages > 1 && (
                <TablePagination
                  rowsPerPage={PAGE_SIZE}
                  currentPage={currentPage}
                  handlePageChange={handlePageChange}
                  totalRecords={segmentData?.total_records}
                  totalPages={segmentData?.total_pages}
                />
              )}
            </TableFooter>
          </>
        ) : !(isLoading || isDeleting || isProcessing || isFetching || isFetchingQueue) && 'No Record Found'}
      </BodyContent>
    </Body>
  )
}
